import { styled } from '@mui/material/styles';
import { Box, Typography, List, ListItem, Container, ContainerProps, TypographyProps } from '@mui/material';

// Main Title
export const MainTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
    marginBottom: '10px',
    fontSize: theme.typography.pxToRem(32),
    fontWeight: 700,
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
        fontSize: theme.typography.pxToRem(40),
    },
}));

// Secondary Text
export const SecondaryText = styled(Typography)(({ theme }) => ({
    marginBottom: '30px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

// Additional Text
export const AdditionalText = styled(Typography)(({ theme }) => ({
    margin: '0 0 10px',
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
}));

// Description Text Box
export const DescriptionTextBox = styled(Box)(({ theme }) => ({
    marginBottom: '30px',
    textAlign: 'left',
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.secondary,
    '& ul, ol': {
        textAlign: 'left',
        margin: '0 auto 12px',
        '& li': {
            marginBottom: '5px',
        },
    },
    '& p': {
        margin: '10px 0',
    },
}));

// Centered Box
export const CenteredBox = styled(Container)<ContainerProps>({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
});

// Submission Review
export const ReviewTitle = styled(Typography)<TypographyProps>(({ theme }) => ({
    marginBottom: '14px',
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
    color: theme.palette.primary.main,
    lineHeight: 1.3,
}));

export const ReviewWrapper = styled(Box)({
    marginBottom: '30px',
});

export const ReviewBox = styled(Box)(({ theme }) => ({
    padding: '25px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:first-of-type': {
        borderTop: `1px solid ${theme.palette.divider}`,
    }
}));

export const ReviewList = styled('ul')({
    margin: 0,
    padding: 0,
    listStyle: 'none',
});

export const ReviewListItem = styled('li')({
    marginBottom: '8px',
    lineHeight: 0,
    '&:last-child': {
        marginBottom: 0,
    },
});

export const ReviewListLabel = styled(Typography)<TypographyProps>(({ theme }) => ({
    fontWeight: 500,
    lineHeight: 1,
    '& .MuiSvgIcon-root': {
        marginRight: '5px',
        position: 'relative',
        top: '4px',
    },
    '&.reviewListLabelDisable, &.reviewListLabelHide': {
        color: theme.palette.text.disabled,
    },
}));

export const ReviewListText = styled(Typography)({
    lineHeight: 1.3,
});

export const ReviewChipItem = styled('span')({
    padding: '2px 5px',
    display: 'inline-block',
    margin: '0 7px 7px 0',
    borderRadius: '4px',
    backgroundColor: '#eff3f9',
    wordBreak: 'break-word',
});

// Button Group
export const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    margin: '0 -5px',
    '& .button-group-item': {
        width: '50%',
        margin: '0 5px',
        [theme.breakpoints.up('sm')]: {
            width: 'inherit',
        },
    },
    '& .button-group-item:first-of-type:last-child': {
        width: 'inherit',
        margin: 0,
    },
    '&.button-group-content': {
        [theme.breakpoints.up('md')]: {
            justifyContent: 'flex-start',
        },
    },
    '@media print': {
        display: 'none',
    },
}));

// Inline List
export const InlineList = styled(List)(({ theme }) => ({
    margin: '0 0 10px',
    padding: 0,
    listStyle: 'none',
}));

export const InlineListItem = styled(ListItem)(({ theme }) => ({
    display: 'inline-block',
    '&::before': {
        content: "''",
        borderLeft: `1px solid ${theme.palette.text.secondary}`,
    },
    '&:first-of-type::before': {
        display: 'none',
    },
}));

export const InlineListLink = styled('a')(({ theme }) => ({
    display: 'inline-block',
    margin: '0 10px',
    color: theme.palette.text.secondary,
    '&:hover': {
        textDecoration: 'none',
    },
}));

// Loader Wrapper
export const LoaderWrapper = styled(Container, {
    shouldForwardProp: (prop) => prop !== 'isLoading',
})<{isLoading?: boolean;} & ContainerProps>(({ theme, isLoading }) => ({
    position: "relative",
    ...(isLoading && {
        '&::after': {
        content: "''",
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1045,
        opacity: 0.7,
        backgroundColor: theme.palette.background.default,
        },
    }),
}));

export const Loader = styled(Box)({
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    zIndex: 1050,
    textAlign: 'center',
    transform: 'translateY(-50%)',
});

export const PayProcessStatusText = styled(Typography)(({ theme }) => ({
    marginBottom: '30px',
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

// Social
export const SocialWrapper = styled(Box)({
    flexShrink: 0,
});

export const HeaderSocialWrapper = styled(Box)(({ theme }) => ({
    position: 'absolute',
    right: '16px',
    bottom: '-40px',
    [theme.breakpoints.up('sm')]: {
        right: '24px',
    },
}));

export const SocialList = styled("ul")({
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    padding: 0,
    listStyle: 'none',
    '& .MuiSvgIcon-root': {
        color: '#b0bac9',
    },
});

export const SocialListItem = styled('li')({
    marginLeft: '7px',
});

export const SocialListLink = styled("a")({
    display: 'flex',
});